import { sum } from 'lodash-es'
import type { RouteSegment, StreetSegmentPlanningData } from '@/api/main/types.gen'

export type PathLengthStats = {
  driven: number
  billable: number
  requested: number
  overheadPercent: number
}

export function computePathLengthStats(
  pathSegments: RouteSegment[],
  segments: Record<string, StreetSegmentPlanningData>,
  drivesBySegment: Record<string, { normal: number; reverse: number }>
): PathLengthStats {
  const driven = sum(pathSegments.map(s => segments[s.street_segment_id]?.length ?? 0))

  let billable = 0
  let requested = 0
  for (const s of Object.values(segments)) {
    const drives = drivesBySegment[s.street_segment_id] ?? { normal: 0, reverse: 0 }
    const directions = s.is_bidirectional ? 2 : 1
    requested += s.length * s.requested_drives * directions
    billable +=
      s.length *
      (Math.min(s.requested_drives, drives.normal) +
        (s.is_bidirectional ? Math.min(s.requested_drives, drives.reverse) : 0))
  }

  return {
    driven,
    billable,
    requested,
    overheadPercent: billable > 0 ? 100 * (driven / billable - 1) : 0,
  }
}

export type ItineraryRow = {
  key: string
  index: number
  segmentId: string
  streetName: string
  lengthLabel: string
}

export function buildItineraryRows(
  pathSegments: RouteSegment[],
  segments: Record<string, StreetSegmentPlanningData>
): ItineraryRow[] {
  const rows: ItineraryRow[] = []
  for (let i = 0; i < pathSegments.length; i++) {
    const seg = pathSegments[i]!
    const meta = segments[seg.street_segment_id]
    rows.push({
      key: `${seg.street_segment_id}-${i}`,
      index: i + 1,
      segmentId: seg.street_segment_id,
      streetName: meta?.street_name ?? '—',
      lengthLabel: meta ? meta.length.toFixed(3) : '—',
    })
  }
  return rows
}
